import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { ChevronRight, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching blog post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-2xl text-stone-900 mb-4">Post not found</h1>
          <Link
            to="/blog"
            className="text-deep-moss font-sans hover:underline"
          >
            Back to blog
          </Link>
        </div>
      </div>
    );
  }

  const formattedDate = format(new Date(post.published_at), "MMMM d, yyyy");

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">
          <nav className="flex items-center gap-2 text-sm font-sans" data-testid="breadcrumb">
            <Link to="/" className="text-stone-500 hover:text-deep-moss">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <Link to="/blog" className="text-stone-500 hover:text-deep-moss">
              Blog
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <span className="text-stone-900 truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img
          src={post.image_url}
          alt={post.title}
          data-testid="blog-post-image"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-12">
        {/* Meta */}
        <div className="mb-8">
          <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-3">
            {formattedDate} · {post.author}
          </p>
          <h1 className="font-serif text-3xl md:text-5xl font-bold text-stone-900 leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
        </div>

        {/* Body */}
        <article className="prose prose-stone max-w-none">
          {post.content.split("\n\n").map((paragraph, index) => {
            // Handle markdown-style bold and headers
            if (paragraph.startsWith("**") && paragraph.endsWith("**")) {
              return (
                <h3 key={index} className="font-serif text-xl font-semibold text-stone-900 mt-8 mb-4">
                  {paragraph.replace(/\*\*/g, "")}
                </h3>
              );
            }
            
            // Handle bullet lists
            if (paragraph.startsWith("- ")) {
              const items = paragraph.split("\n").filter(item => item.startsWith("- "));
              return (
                <ul key={index} className="list-disc list-inside space-y-2 text-stone-700 font-sans">
                  {items.map((item, i) => (
                    <li key={i}>{item.replace("- ", "")}</li>
                  ))}
                </ul>
              );
            }

            // Handle numbered lists
            if (/^\d+\./.test(paragraph)) {
              const items = paragraph.split("\n").filter(item => /^\d+\./.test(item));
              return (
                <ol key={index} className="list-decimal list-inside space-y-2 text-stone-700 font-sans">
                  {items.map((item, i) => (
                    <li key={i}>{item.replace(/^\d+\.\s*/, "")}</li>
                  ))}
                </ol>
              );
            }

            // Regular paragraphs
            return (
              <p key={index} className="text-stone-700 font-sans leading-relaxed mb-4">
                {paragraph.replace(/\*\*(.*?)\*\*/g, (match, p1) => p1)}
              </p>
            );
          })}
        </article>

        {/* Back Link */}
        <div className="mt-12 pt-8 border-t border-stone-200">
          <Link
            to="/blog"
            data-testid="back-to-blog-link"
            className="inline-flex items-center gap-2 text-deep-moss font-sans text-sm hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    </div>
  );
};
